import React, { useEffect, useMemo, useState } from 'react';
import { FaFacebook, FaLink, FaSave, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Modal from '../../../components/Modal';
import { fetchAllProducts } from '../controllers/products-controller';
import {
  deleteTrackedFacebookPost,
  fetchFacebookLeadSettings,
  fetchFacebookPosts,
  fetchTrackedFacebookPosts,
  saveFacebookLeadSettings,
  saveTrackedFacebookPost,
} from './controller/facebook-leads-controller';

const emptySettings = {
  facebookPageId: '',
  facebookAccessToken: '',
};

const FacebookLeadsPage = () => {
  const [settings, setSettings] = useState(emptySettings);
  const [posts, setPosts] = useState([]);
  const [trackedPosts, setTrackedPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingPostId, setSavingPostId] = useState(null);
  const [error, setError] = useState('');
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');

  const trackedByPostId = useMemo(() => {
    return trackedPosts.reduce((acc, item) => {
      acc[item.facebookPostId] = item;
      return acc;
    }, {});
  }, [trackedPosts]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      const [settingsData, trackedData, productsData] = await Promise.all([
        fetchFacebookLeadSettings(),
        fetchTrackedFacebookPosts(),
        fetchAllProducts(),
      ]);

      setSettings({
        facebookPageId: settingsData.facebookPageId || '',
        facebookAccessToken: '',
      });
      setTrackedPosts(trackedData || []);
      setProducts(productsData || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Impossible de charger la configuration Facebook.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setError('');
      const result = await saveFacebookLeadSettings(settings);
      setSettings({
        facebookPageId: result.facebookPageId || '',
        facebookAccessToken: '',
      });
      toast.success('Paramètres Facebook enregistrés');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Échec de l’enregistrement des paramètres Facebook');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLoadPosts = async () => {
    try {
      setLoadingPosts(true);
      setError('');
      const fetchedPosts = await fetchFacebookPosts(settings);
      setPosts(fetchedPosts || []);
      toast.success(`${(fetchedPosts || []).length} post(s) chargé(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Impossible de récupérer les posts Facebook');
    } finally {
      setLoadingPosts(false);
    }
  };

  const openTrackModal = (post) => {
    setSelectedPost(post);
    setSelectedProductId(String(trackedByPostId[post.id]?.productId || ''));
    setIsTrackModalOpen(true);
  };

  const handleTrackPost = async () => {
    if (!selectedPost) {
      return;
    }

    if (!selectedProductId) {
      toast.warning('Sélectionnez un produit avant d’enregistrer le post');
      return;
    }

    try {
      setSavingPostId(selectedPost.id);
      const saved = await saveTrackedFacebookPost({
        id: trackedByPostId[selectedPost.id]?.id || null,
        facebookPostId: selectedPost.id,
        postMessage: selectedPost.message || '',
        permalinkUrl: selectedPost.permalinkUrl || '',
        productId: Number(selectedProductId),
        isActive: true,
      });

      setTrackedPosts((current) => {
        const remaining = current.filter((item) => item.id !== saved.id);
        return [saved, ...remaining];
      });
      setIsTrackModalOpen(false);
      setSelectedPost(null);
      setSelectedProductId('');
      toast.success('Post enregistré pour le suivi');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Impossible d’enregistrer ce post');
    } finally {
      setSavingPostId(null);
    }
  };

  const handleDeleteTracked = async (trackedId) => {
    try {
      await deleteTrackedFacebookPost(trackedId);
      setTrackedPosts((current) => current.filter((item) => item.id !== trackedId));
      toast.success('Post retiré du suivi');
    } catch (err) {
      toast.error('Impossible de supprimer ce post suivi');
    }
  };

  const trackedLookup = useMemo(() => new Set(trackedPosts.map((item) => item.facebookPostId)), [trackedPosts]);

  if (loading) {
    return (
      <div className="px-6 py-10">
        <div className="mx-auto max-w-6xl rounded-3xl border border-white/60 bg-white/80 p-10 shadow-[0_20px_80px_rgba(16,24,40,0.08)] backdrop-blur">
          <div className="h-6 w-40 animate-pulse rounded bg-slate-200" />
          <div className="mt-6 h-80 animate-pulse rounded-2xl bg-slate-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facebook Leads</h1>
            <p className="mt-2 text-sm text-gray-700">
              Associer des posts Facebook à un produit et créer automatiquement des commandes abandonnées depuis les commentaires.
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-3 sm:mt-0">
            <button
              type="button"
              onClick={() => setIsSettingsModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
            >
              Paramètres
            </button>
            <button
              type="button"
              onClick={handleLoadPosts}
              disabled={loadingPosts}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FaLink className="mr-2 h-4 w-4" />
              {loadingPosts ? 'Chargement...' : 'Charger les posts'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Posts chargés</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{posts.length}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Posts suivis</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{trackedPosts.length}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Mode</div>
            <div className="mt-2 text-lg font-semibold text-[#174540]">Polling automatique</div>
          </div>
        </div>

        <section className="mt-6 rounded-lg bg-white shadow overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Posts Facebook</h2>
            <p className="mt-1 text-sm text-gray-500">Ouvrez le popup pour associer un produit à chaque post.</p>
          </div>

          {posts.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Chargez les posts de la page pour commencer.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {posts.map((post) => {
                const tracked = trackedLookup.has(post.id);
                return (
                  <article key={post.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-medium text-gray-900 truncate max-w-[20rem]">{post.id}</h3>
                          {tracked && (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Suivi</span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">{post.createdTime ? new Date(post.createdTime).toLocaleString() : 'Post Facebook'}</div>
                        <p className="mt-2 text-sm text-gray-700 line-clamp-2">{post.message || 'Aucun message disponible pour ce post.'}</p>
                        {post.permalinkUrl && (
                          <a href={post.permalinkUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center text-sm text-[#174540] hover:underline">
                            Ouvrir le post <FaLink className="ml-1 h-3 w-3" />
                          </a>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => openTrackModal(post)}
                        disabled={savingPostId === post.id}
                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-[#174540] px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-[#174540] disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {tracked ? 'Modifier le suivi' : 'Suivre ce post'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-6 rounded-lg bg-white shadow overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Posts suivis</h2>
          </div>
          {trackedPosts.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">Aucun post n’est encore suivi.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Post</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Produit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Dernier commentaire</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">État</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {trackedPosts.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">{item.facebookPostId}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <div className="font-medium text-gray-900">{item.productName || 'Produit introuvable'}</div>
                        <div className="text-xs text-gray-500">{item.productReference || ''}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{item.lastCommentAt ? new Date(item.lastCommentAt).toLocaleString() : 'Aucun'}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                          {item.isActive ? 'Actif' : 'Suspendu'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => handleDeleteTracked(item.id)}
                          className="inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50"
                        >
                          <FaTrash className="mr-2 h-4 w-4" /> Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <Modal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        title="Paramètres Facebook"
        size="lg"
        onSubmit={handleSaveSettings}
        submitLabel={savingSettings ? 'Enregistrement...' : 'Enregistrer'}
        isSubmitting={savingSettings}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Page ID</span>
            <input
              value={settings.facebookPageId}
              onChange={(e) => setSettings((current) => ({ ...current, facebookPageId: e.target.value }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#174540] focus:outline-none focus:ring-2 focus:ring-[#174540]/20"
              placeholder="1234567890"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Access Token</span>
            <input
              type="password"
              value={settings.facebookAccessToken}
              onChange={(e) => setSettings((current) => ({ ...current, facebookAccessToken: e.target.value }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#174540] focus:outline-none focus:ring-2 focus:ring-[#174540]/20"
              placeholder="Token de la page"
            />
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={isTrackModalOpen}
        onClose={() => {
          setIsTrackModalOpen(false);
          setSelectedPost(null);
          setSelectedProductId('');
        }}
        title="Associer le produit au post"
        size="lg"
        onSubmit={handleTrackPost}
        submitLabel={savingPostId ? 'Enregistrement...' : 'Enregistrer le suivi'}
        isSubmitting={Boolean(savingPostId)}
      >
        <div className="space-y-4">
          <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-700">
            <div className="font-medium text-gray-900">{selectedPost?.id}</div>
            <div className="mt-2 text-gray-600">{selectedPost?.message || 'Aucun message disponible.'}</div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Produit lié</span>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#174540] focus:outline-none focus:ring-2 focus:ring-[#174540]/20"
            >
              <option value="">Sélectionnez un produit</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.reference ? `${product.reference} - ${product.name}` : product.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default FacebookLeadsPage;